// Bevestigingsvragen per regel (blok C deel 2). Los bestand zodat types.ts de schema's kan gebruiken
// zonder kringverwijzing, net als opleveringSchema.ts.
//
//  - extra offerte (SPEC_workflow 4): posten uit een offerte die na de opstartfiche verkocht werd;
//    ze tellen pas mee na bevestiging.
//  - eenheidsvraag (SPEC_calculatie 8.1): de eenheid op de fiche wijkt af van de prijslijst; de post
//    telt pas mee na bevestiging van de hoeveelheid in de eenheid van de prijslijst.
//  - extra informatie (SPEC_calculatie 8, 9): de post komt uit het vrije tekstveld van de fiche.
import { z } from 'zod';

export const vraagStatusSchema = z.enum(['open', 'bevestigd']);
export type VraagStatus = z.infer<typeof vraagStatusSchema>;

export const extraOfferteBronSchema = z.object({
  offerteId: z.number().int(),
  nummer: z.string(),
  datum: z.string().nullable(),
  /** P-nummer van de offerte als die onder een ander project van dezelfde klant hangt. */
  projectCode: z.string().nullable().optional(),
  status: vraagStatusSchema,
  beslistDoor: z.string().nullable().optional(),
  beslistOp: z.string().nullable().optional(),
});
export type ExtraOfferteBron = z.infer<typeof extraOfferteBronSchema>;

export const eenheidVraagSchema = z.object({
  ficheEenheid: z.string(),
  prijslijstEenheid: z.string(),
  /** Hoeveelheid zoals ze op de fiche stond, in de eenheid van de fiche. */
  ficheHoeveelheid: z.number(),
  status: vraagStatusSchema,
  beslistDoor: z.string().nullable().optional(),
  beslistOp: z.string().nullable().optional(),
  /** Id van de geleerde regel die de vraag automatisch beantwoordde (SPEC_calculatie 10.4). */
  regelId: z.string().nullable().optional(),
});
export type EenheidVraag = z.infer<typeof eenheidVraagSchema>;

export const extraInformatieBronSchema = z.object({
  /** Het stuk tekst uit extra informatie waaruit de post herkend werd. */
  fragment: z.string(),
  /** Id van de geleerde regel als de post automatisch toegepast werd (SPEC_calculatie 10.4). */
  regelId: z.string().nullable().optional(),
  beslistDoor: z.string().nullable().optional(),
  beslistOp: z.string().nullable().optional(),
});
export type ExtraInformatieBron = z.infer<typeof extraInformatieBronSchema>;

/** Beslissing per extra offerte op projectniveau, zodat verversen ze niet opnieuw voorstelt (4, 7.3). */
export const extraOfferteBeslissingSchema = z.object({
  offerteId: z.number().int(),
  nummer: z.string(),
  datum: z.string().nullable(),
  bedrag: z.number().nullable().optional(),
  projectCode: z.string().nullable().optional(),
  beslissing: z.enum(['open', 'bevestigd', 'geweigerd']),
  door: z.string().nullable().optional(),
  op: z.string().nullable().optional(),
});
export type ExtraOfferteBeslissing = z.infer<typeof extraOfferteBeslissingSchema>;

/** De opstartfiche waarop de calculatie gebaseerd is (7.3 verversen, 2.1 verschil onderaannemer). */
export const ficheBronSchema = z.object({
  bron: z.enum(['bouwflow', 'pdf']),
  sheetId: z.number().int().nullable(),
  /** updated_at van de fiche in Bouwflow toen de calculatie gemaakt werd. */
  gewijzigdOp: z.string().nullable(),
  aangemaaktOp: z.string().nullable().optional(),
  /** Onderaannemer op de opstartfiche (PDF of Bouwflow) en volgens Bouwflow, als ze verschillen (2.1). */
  onderaannemerVerschil: z.object({ fiche: z.string(), bouwflow: z.string() }).nullable().optional(),
  /** Opleveringsbon op het moment van de calculatie (7.3). */
  oplevering: z.object({ id: z.number().int().nullable(), status: z.string().nullable(), getekendOp: z.string().nullable() }).nullable().optional(),
});
export type FicheBron = z.infer<typeof ficheBronSchema>;
